import { useEffect, useMemo } from "react";

import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";

import { Apartment, Device, Room } from "../../../graphql/models";
import { QUERIES } from "../../../graphql";
import { useSnackbarLazyQuery } from "../../../hooks/useSnackbarLazyQuery";

import useApartmentRoomsEditorStyle from "./apartment_rooms_editor.style";
import { CircularProgress } from "@material-ui/core";
import { RoomDevicesEditor } from "../room_devices_editor";
import { RoomDataEditor } from "../room_data_editor";

export interface ApartmentRoomsEditorProps {
  apartment: Apartment;
}

export function ApartmentRoomsEditor(props: ApartmentRoomsEditorProps) {
  const { apartment } = props;
  const classes = useApartmentRoomsEditorStyle();

  const [invokeGetRooms, { data, loading, refetch }] = useSnackbarLazyQuery(
    QUERIES.ROOMS,
    {
      fetchPolicy: "network-only",
      nextFetchPolicy: "cache-and-network",
    }
  );

  const rooms: Room[] = useMemo(() => {
    if (data) {
      return data.rooms;
    }

    return void 0;
  }, [data]);

  const onRoomUpdated = (updatedRoom: Room) => {
    if (refetch) {
      refetch();
    }
  };

  const onDeviceUpdated = (updatedDevice: Device) => {
    if (refetch) {
      refetch();
    }
  };

  useEffect(() => {
    if (apartment?.id) {
      invokeGetRooms({
        variables: {
          apartmentId: apartment.id,
        },
      });
    }
  }, [apartment, invokeGetRooms]);

  return (
    <Paper elevation={3} style={{ width: "100%", padding: "1rem" }}>
      <Typography variant="h4" color="textSecondary">
        Stanze
      </Typography>
      <br />
      {loading && <CircularProgress />}
      {!loading && rooms?.length > 0 && (
        <Grid container spacing={3}>
          {rooms.map((room) => {
            return (
              <Grid
                key={room.name}
                spacing={1}
                item
                xl={3}
                lg={4}
                md={6}
                xs={12}
              >
                <Card className={classes.card} variant="outlined">
                  <CardContent>
                    <Typography variant="h5" color="textSecondary" gutterBottom>
                      {room.name}
                    </Typography>
                    <RoomDataEditor
                      apartment={apartment}
                      room={room}
                      onRoomUpdated={onRoomUpdated}
                    />
                    <RoomDevicesEditor
                      apartment={apartment}
                      room={room}
                      onDeviceUpdated={onDeviceUpdated}
                    />
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Paper>
  );
}
